import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { ArrowLeft, Newspaper, Lock, ChevronRight, Zap, Clock, CheckCircle, XCircle, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function WeeklyBrief() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brief, setBrief] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [tab, setTab] = useState("latest");

  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);

  useEffect(() => {
    loadBrief();
  }, []);

  const loadBrief = async () => {
    try {
      const res = await api.get("/briefs/latest");
      setBrief(res.data.brief);
      setPreviewOnly(res.data.preview_only || false);
    } catch {}
    try {
      const res = await api.get("/briefs/archive");
      setArchive(res.data.briefs || []);
    } catch {}
    setLoading(false);
  };

  const loadQuiz = async () => {
    if (!brief?.id) return;
    setQuizLoading(true);
    try {
      const res = await api.get(`/briefs/${brief.id}/quiz`);
      setQuiz(res.data.quiz);
      setQuizCompleted(res.data.completed || false);
      setShowQuiz(true);
      setCurrentQ(0);
      setAnswers({});
      setQuizResults(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load quiz");
    }
    setQuizLoading(false);
  };

  const handleAnswer = (answer) => {
    setAnswers(prev => ({ ...prev, [currentQ]: answer }));
    if (currentQ < (quiz?.questions?.length || 0) - 1) {
      setTimeout(() => setCurrentQ(prev => prev + 1), 400);
    }
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    const submission = Object.entries(answers).map(([idx, answer]) => ({
      question_index: parseInt(idx),
      answer
    }));
    try {
      const res = await api.post(`/briefs/${brief.id}/quiz/submit`, { answers: submission });
      setQuizResults(res.data);
      if (res.data.xp_earned > 0) {
        toast.success(`+${res.data.xp_earned} BTC XP earned!`);
      }
    } catch (err) {
      toast.error("Failed to submit quiz");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = brief?.sections || {};
  const SECTION_ORDER = [
    { key: "one_line_summary", label: "ONE LINE SUMMARY" },
    { key: "the_number", label: "THE NUMBER" },
    { key: "the_market", label: "THE MARKET" },
    { key: "the_macro", label: "THE MACRO" },
    { key: "the_institutions", label: "THE INSTITUTIONS" },
    { key: "on_chain", label: "ON CHAIN" },
    { key: "the_political", label: "THE POLITICAL" },
    { key: "prediction_watch", label: "PREDICTION WATCH" },
    { key: "the_voices", label: "THE VOICES" },
    { key: "bottom_line", label: "BOTTOM LINE" },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="weekly-brief-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">WEEKLY <span className="text-[#F7931A]">BRIEF</span></h1>
          <Newspaper className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("latest")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "latest" ? "bg-[#F7931A] text-black" : "bg-[#161616] text-zinc-400"}`}
            data-testid="tab-latest">
            Latest
          </button>
          <button onClick={() => setTab("archive")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "archive" ? "bg-[#F7931A] text-black" : "bg-[#161616] text-zinc-400"}`}
            data-testid="tab-archive">
            Archive
          </button>
        </div>

        {tab === "latest" && (
          <>
            {/* Explainer */}
            <div className="card-dark p-5 mb-4 border border-[#F7931A]/20" data-testid="weekly-brief-explainer">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Newspaper className="text-[#F7931A]" size={20} />
                </div>
                <div>
                  <h2 className="text-white font-heading text-base font-bold mb-1">Intelligence Briefs</h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    AI-generated weekly intelligence briefs covering current events in Bitcoin — market moves, institutional adoption,
                    on-chain data, macro trends, and political developments. Published every Monday at 8am UTC.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3 text-xs">
                    <span className="flex items-center gap-1 text-[#F7931A]">
                      <Zap size={12} /> Up to 300 BTC XP per quiz
                    </span>
                    {user?.purchase_tier === "free" && (
                      <span className="flex items-center gap-1 text-yellow-500">
                        <Lock size={12} /> Full brief for paid members
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Clock size={12} /> New brief every Monday
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {!brief ? (
              <div className="text-center py-16 space-y-4" data-testid="no-brief">
                <Newspaper className="text-zinc-700 mx-auto" size={48} />
                <h2 className="text-white font-heading text-2xl font-bold">No Brief Yet</h2>
                <p className="text-zinc-500 text-sm">The Bitcoin Intelligence Brief publishes every Monday at 8am UTC. Check back soon!</p>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="brief-content">
                <div className="card-dark p-6 border-[#F7931A]/30">
                  <h2 className="font-heading text-2xl font-bold text-white mb-2">{brief.title}</h2>
                  <div className="flex items-center gap-4 text-zinc-500 text-xs">
                    <span className="flex items-center gap-1"><Clock size={12} /> {new Date(brief.published_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><Zap size={12} className="text-[#F7931A]" /> Quiz: up to 300 BTC XP</span>
                  </div>
                </div>

                {previewOnly ? (
                  <>
                    <div className="card-dark p-6 space-y-6">
                      {SECTION_ORDER.slice(0, 2).map(({ key, label }) => {
                        const content = sections[key];
                        if (!content) return null;
                        return (
                          <div key={key}>
                            <h3 className="font-heading text-[#F7931A] text-sm font-bold mb-2 tracking-wide">{label}</h3>
                            <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{content}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="card-dark p-6 text-center border-[#F7931A]/30">
                      <Lock className="text-[#F7931A] mx-auto mb-3" size={24} />
                      <p className="text-white font-medium">Full Brief — Paid Members Only</p>
                      <p className="text-zinc-500 text-sm mt-1">Purchase Bitcoin Brain to read the complete weekly intelligence brief with all 10 sections.</p>
                      <Button onClick={() => navigate("/pricing")} className="btn-primary mt-4" data-testid="upgrade-brief-btn">
                        See Plans <ChevronRight size={16} />
                      </Button>
                    </div>
                  </>
                ) : sections.full_text || sections.one_line_summary ? (
                  <>
                    <div className="card-dark p-6 space-y-6">
                      {SECTION_ORDER.map(({ key, label }) => {
                        const content = sections[key];
                        if (!content) return null;
                        return (
                          <div key={key}>
                            <h3 className="font-heading text-[#F7931A] text-sm font-bold mb-2 tracking-wide">{label}</h3>
                            <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{content}</p>
                          </div>
                        );
                      })}
                      {!sections.one_line_summary && sections.full_text && (
                        <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">
                          {sections.full_text}
                        </div>
                      )}
                      {!sections.one_line_summary && sections.full_text && sections.full_text.includes("unavailable") && (
                        <div className="text-center pt-4 border-t border-white/5">
                          <p className="text-zinc-500 text-xs">This brief was generated with limited data. A fresh brief will be published automatically next Monday.</p>
                        </div>
                      )}
                    </div>

                    {/* Brief Quiz CTA */}
                    {!showQuiz && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="card-dark p-6 border-[#F7931A]/20" data-testid="brief-quiz-cta">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-[#F7931A]/10 flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="text-[#F7931A]" size={24} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-medium">Brief Quiz</h3>
                            <p className="text-zinc-500 text-sm">Test your comprehension and earn up to 300 BTC XP</p>
                          </div>
                          <Button onClick={loadQuiz} disabled={quizLoading} className="btn-primary" data-testid="start-brief-quiz-btn">
                            {quizLoading ? "Loading..." : quizCompleted ? "Retake" : "Take Quiz"}
                          </Button>
                        </div>
                      </motion.div>
                    )}

                    {/* Brief Quiz UI */}
                    {showQuiz && (
                      <AnimatePresence mode="wait">
                        {!quiz ? (
                          <motion.div key="no-quiz" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-dark p-8 text-center">
                            <p className="text-zinc-400">No quiz available for this brief yet. Check back later.</p>
                            <Button onClick={() => setShowQuiz(false)} className="btn-secondary mt-4">Close</Button>
                          </motion.div>
                        ) : quizResults ? (
                          <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-dark p-6 text-center" data-testid="brief-quiz-results">
                            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${quizResults.passed ? "bg-green-500/20" : "bg-red-500/20"}`}>
                              {quizResults.passed ? <CheckCircle className="text-green-400" size={32} /> : <XCircle className="text-red-400" size={32} />}
                            </div>
                            <h3 className="text-white font-heading text-2xl font-bold mb-2">
                              {quizResults.passed ? "Passed!" : "Keep Learning"}
                            </h3>
                            <p className="text-zinc-400 mb-4">{quizResults.correct}/{quizResults.total} correct</p>
                            {quizResults.xp_earned > 0 && (
                              <p className="text-[#F7931A] font-mono font-bold text-lg mb-4">+{quizResults.xp_earned} BTC XP</p>
                            )}
                            <Button onClick={() => { setShowQuiz(false); setQuizResults(null); }} className="btn-secondary">Close</Button>
                          </motion.div>
                        ) : (
                          <motion.div key="quiz" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-dark p-6" data-testid="brief-quiz-active">
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-zinc-500 text-sm">Question {currentQ + 1}/{quiz.questions.length}</span>
                              <span className="text-[#F7931A] text-sm font-mono">Up to 300 BTC XP</span>
                            </div>
                            <p className="text-white text-lg font-medium mb-6" data-testid="brief-quiz-question">{quiz.questions[currentQ]?.question}</p>
                            <div className="space-y-2">
                              {[quiz.questions[currentQ]?.correct, ...(quiz.questions[currentQ]?.wrong || [])].sort().map((opt, i) => (
                                <button key={i} onClick={() => handleAnswer(opt)}
                                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                                    answers[currentQ] === opt
                                      ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                                      : "border-white/10 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/50"
                                  }`}
                                  data-testid={`brief-quiz-option-${i}`}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                            <div className="flex justify-between mt-6">
                              <Button onClick={() => setShowQuiz(false)} className="btn-secondary" size="sm">Cancel</Button>
                              {Object.keys(answers).length === quiz.questions.length && (
                                <Button onClick={submitQuiz} className="btn-primary" data-testid="submit-brief-quiz-btn">
                                  Submit Quiz
                                </Button>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </>
                ) : (
                  <div className="card-dark p-8 text-center space-y-4" data-testid="brief-empty">
                    <Newspaper className="text-zinc-700 mx-auto" size={40} />
                    <p className="text-zinc-400 text-sm">This brief doesn't have full content yet. A new brief will be published automatically next Monday.</p>
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}

        {tab === "archive" && (
          <div className="space-y-2" data-testid="brief-archive">
            {archive.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No archived briefs yet.</p>
            ) : archive.map((b) => (
              <div key={b.id} className="card-dark p-4 flex items-center justify-between cursor-pointer hover:border-[#F7931A]/30"
                onClick={() => { setBrief(b); setTab("latest"); setShowQuiz(false); setQuizResults(null); }}
              >
                <div>
                  <p className="text-white text-sm font-medium">{b.title}</p>
                  <p className="text-zinc-500 text-xs">{new Date(b.published_at).toLocaleDateString()}</p>
                </div>
                <ChevronRight className="text-zinc-600" size={18} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
